import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="watchlist"
//
// Presentational only: type-filtering and re-sorting of the watch cards, and
// showing / hiding the add-watch and per-watch detail drawers. Real changes
// (pause/resume a watch, add a watch) are plain form posts that go through the
// existing settings/signal_sources actions — no business logic lives here.
export default class extends Controller {
  static targets = [
    "card", "list", "sortButton",
    "addDrawer", "sourceTypeField", "input", "inputLabel", "topicNote"
  ]

  // Input label / placeholder per watch kind. The create action reads params[:url]
  // for post/person/company and params[:search_keywords] for a topic, so the
  // input's name switches accordingly.
  KINDS = {
    post_url:       { name: "url", label: "Paste the post link", placeholder: "https://www.linkedin.com/posts/..." },
    profile:        { name: "url", label: "Profile link or name", placeholder: "https://www.linkedin.com/in/... or a name" },
    company_page:   { name: "url", label: "Company page or name", placeholder: "https://www.linkedin.com/company/... or a name" },
    keyword_search: { name: "search_keywords", label: "Keywords to watch", placeholder: 'e.g. "fincrime hiring" OR "AML lead"' }
  }

  connect() {
    this.kind = "all"
    this.sort = "highfit"
  }

  // ----- filter / sort (list) ------------------------------------------------

  filter(event) {
    this.kind = event.params.kind

    this.element.querySelectorAll(".wl-filter").forEach((btn) => {
      btn.classList.toggle("wl-filter-active", btn.dataset.watchlistKindParam === this.kind)
    })

    this.cardTargets.forEach((card) => {
      const show = this.kind === "all" || card.dataset.kind === this.kind
      card.style.display = show ? "" : "none"
    })
  }

  toggleSort() {
    this.sort = this.sort === "highfit" ? "newest" : "highfit"
    if (this.hasSortButtonTarget) {
      this.sortButtonTarget.textContent =
        this.sort === "highfit" ? "⇅ Most high-fit" : "⇅ Newest finds"
    }

    const key = this.sort === "highfit" ? "highFit" : "lastFind"
    const cards = [...this.cardTargets].sort(
      (a, b) => Number(b.dataset[key] || 0) - Number(a.dataset[key] || 0)
    )
    cards.forEach((card) => this.listTarget.appendChild(card))
  }

  // ----- add-watch drawer ----------------------------------------------------

  openAdd() {
    if (!this.hasAddDrawerTarget) return
    this.closeDetail()
    this.addDrawerTarget.classList.remove("hidden")
    if (this.hasInputTarget) this.inputTarget.focus()
  }

  closeAdd() {
    if (this.hasAddDrawerTarget) this.addDrawerTarget.classList.add("hidden")
  }

  // Empty-state seed suggestion: open the add drawer with a kind preselected.
  seedAdd(event) {
    this.openAdd()
    this.selectKind(event)
  }

  // Pick a watch kind: reflect it into the hidden source_type field and adapt the
  // single input (name / label / placeholder). The topic note only shows for a
  // topic watch.
  selectKind(event) {
    const sourceType = event.params.sourceType
    const meta = this.KINDS[sourceType]
    if (!meta) return

    if (this.hasSourceTypeFieldTarget) this.sourceTypeFieldTarget.value = sourceType
    if (this.hasInputTarget) {
      this.inputTarget.name = meta.name
      this.inputTarget.placeholder = meta.placeholder
      this.inputTarget.value = ""
    }
    if (this.hasInputLabelTarget) this.inputLabelTarget.textContent = meta.label
    if (this.hasTopicNoteTarget) this.topicNoteTarget.classList.toggle("hidden", sourceType !== "keyword_search")

    this.element.querySelectorAll(".wl-kind").forEach((el) => {
      el.classList.toggle("wl-kind-active", el.dataset.watchlistSourceTypeParam === sourceType)
    })
  }

  // NOTE deliberately NO client-side title derivation: the slug parsing lives
  // ONLY in Core::Linkedin::PostTitleFromUrl, where it is tested. A JS mirror
  // of that logic existed briefly and was removed by decision — two copies of
  // parsing logic, one in a language we cannot test, is how twins drift (the
  // KI-205 shape). The title field shows a generic placeholder; the server
  // derives the real title on save.

  // ----- per-watch detail drawer --------------------------------------------

  openDetail(event) {
    // Ignore clicks that act on their own (toggle form, links) or happen inside
    // an already-open drawer.
    if (event.target.closest(".wl-detail")) return
    if (event.target.closest("form, a")) return

    this.closeDetail()
    const detail = event.currentTarget.querySelector(".wl-detail")
    if (detail) detail.classList.remove("hidden")
  }

  closeDetail(event) {
    if (event) event.stopPropagation()
    this.element.querySelectorAll(".wl-detail").forEach((d) => d.classList.add("hidden"))
  }

  // Close any open drawer on Escape.
  keydown(event) {
    if (event.key === "Escape") {
      this.closeAdd()
      this.closeDetail()
    }
  }
}
